import { useMemo, useCallback } from 'react'
import { formatPrice as formatPriceIntl } from '../../../common/utils'
import { CalendarUtils } from '../calendar-utils'
import { AvailabilityDataRecord } from '../../../types'

export interface TooltipInfo {
  message: string
  dateStr: string
}

export interface CalendarMonthProps {
  year: number
  month: number
  calendarUtils: CalendarUtils
  availability: AvailabilityDataRecord
  showPrice: boolean
  showBookedPrice: boolean
  currency: string
  // #1541: BCP-47 tag for the widget's resolved locale, or undefined for
  // English/unset/unusable — CalendarGrid resolves it once via
  // `resolveLocaleTag` (common/locale-format) so this stays a plain pass-through.
  locale?: string
  weekdayLabels: string[]
  firstDayOfWeek?: number
  monthLabel: string
  allowSelectDates: boolean
  selectedCheckin: string | null
  selectedCheckout: string | null
  hoveredDate: string | null
  onDayClick?: (dateStr: string, element: HTMLElement) => void
  onDayHover?: (dateStr: string | null, element?: HTMLElement) => void
  // #1548 keyboard access. `focusedDate` is the grid's single roving tabstop —
  // it is a widget-wide value, so at most one cell across all rendered months
  // is tabbable and Tab moves past the whole calendar in one press.
  focusedDate?: string | null
  onDayKeyDown?: (dateStr: string, e: React.KeyboardEvent<HTMLElement>) => void
  onDayFocus?: (dateStr: string, element: HTMLElement) => void
  /** The day the open tip describes, so that cell can point at it with aria-describedby. */
  tooltipDate?: string | null
  tooltipId?: string
}

// Narrow currency symbol ("$200", never "US$200" on en-GB/en — #599).
// #1541: `locale` is the pre-resolved BCP-47 tag from CalendarGrid; undefined
// keeps this call byte-identical to the pre-#1541 no-locale invocation.
function formatPrice(price: number, currency: string, locale?: string): string {
  return formatPriceIntl(Math.round(price), currency, locale)
}

export default function CalendarMonth({
  year,
  month,
  calendarUtils,
  availability,
  showPrice,
  showBookedPrice,
  currency,
  locale,
  weekdayLabels,
  firstDayOfWeek = 0,
  monthLabel,
  allowSelectDates,
  selectedCheckin,
  selectedCheckout,
  hoveredDate,
  onDayClick,
  onDayHover,
  focusedDate,
  onDayKeyDown,
  onDayFocus,
  tooltipDate,
  tooltipId,
}: CalendarMonthProps) {
  const daysInMonth = calendarUtils.getMonthDayCount(year, month)
  // Luxon weekday: 1=Monday, 7=Sunday. Map to 0=Sunday..6=Saturday, then shift by the
  // configured first day of week so the grid columns line up with the (reordered) header.
  const firstWeekday = calendarUtils.getFirstWeekday(year, month)
  const dow = firstWeekday % 7 // Sun=0, Mon=1, ... Sat=6
  const startOffset = (dow - firstDayOfWeek + 7) % 7

  const getDayClassNames = useCallback((dateStr: string): string => {
    const classes: string[] = ['day', 'day-of-month']
    const isUnavail = calendarUtils.isUnavailable(dateStr)
    const opts = calendarUtils.dateOptions(dateStr)

    if (isUnavail || !opts) {
      classes.push('disabled', 'unavailable')
      return classes.join(' ')
    }

    // ADVANCE-NOTICE WINDOW (#1071): too soon to book. No check-in inside the
    // window means no reachable check-out either, so the day is fully disabled;
    // the tooltip explains the window instead of a generic "unavailable".
    if (calendarUtils.isNoticeBlocked(dateStr)) {
      classes.push('disabled', 'notice-blocked')
      if (calendarUtils.isToday(dateStr)) {
        classes.push('today')
      }
      return classes.join(' ')
    }

    const isTrapped = calendarUtils.isTrapped(dateStr)
    const canCI = calendarUtils._canCheckinFlag(dateStr)
    const canCO = calendarUtils._canCheckoutFlag(dateStr)
    const canStay = calendarUtils._canStay(dateStr)
    const ciBooked = calendarUtils._checkinBooked(dateStr)
    const coBooked = calendarUtils._checkoutBooked(dateStr)

    if (isTrapped) {
      classes.push('trapped')
      if (ciBooked) classes.push('checkin-booked')
      else if (!canCI) classes.push('checkin-blocked')
      if (coBooked) classes.push('checkout-booked')
      else if (!canCO) classes.push('checkout-blocked')
      return classes.join(' ')
    }

    // Selection state (only when allowSelectDates)
    if (allowSelectDates && selectedCheckin) {
      if (dateStr === selectedCheckin) {
        classes.push('selected', 'checkin', 'range-start')
      } else if (selectedCheckout && dateStr === selectedCheckout) {
        classes.push('selected', 'checkout', 'range-end')
      } else if (selectedCheckout) {
        // Check if date is between checkin and checkout
        const dayDate = calendarUtils.getDateZero(dateStr)
        const ciDate = calendarUtils.getDateZero(selectedCheckin)
        const coDate = calendarUtils.getDateZero(selectedCheckout)
        if (dayDate > ciDate && dayDate < coDate) {
          classes.push('selected', 'range-middle')
        }
      }

      // Hover preview (only when we have checkin but no checkout)
      if (!selectedCheckout && hoveredDate) {
        const dayDate = calendarUtils.getDateZero(dateStr)
        const ciDate = calendarUtils.getDateZero(selectedCheckin)
        const hoverDate = calendarUtils.getDateZero(hoveredDate)
        if (hoverDate > ciDate && dayDate > ciDate && dayDate < hoverDate) {
          classes.push('hover')
        }
        if (dateStr === hoveredDate && hoverDate > ciDate) {
          classes.push('hover-range-end')
        }
      }

      // Selectable range constraints
      if (!selectedCheckout && dateStr !== selectedCheckin) {
        const range = calendarUtils.getSelectableRange(selectedCheckin)
        if (range) {
          const dayDate = calendarUtils.getDateZero(dateStr)
          const ciDate = calendarUtils.getDateZero(selectedCheckin)
          const minDate = calendarUtils.getDateZero(range.minDate)
          const maxDate = calendarUtils.getDateZero(range.maxDate)
          if (dayDate < minDate || dayDate > maxDate) {
            classes.push('disabled')
            // Inside the min-stay window (#603, WP parity): grayed with a
            // not-allowed cursor; hovering it shows the min-stay tooltip.
            if (dayDate > ciDate && dayDate < minDate) {
              classes.push('minstay-blocked')
            }
            return classes.join(' ')
          }
        }
      }
    }

    // Base availability states
    if (canCI && canCO && canStay) {
      classes.push('selectable', 'checkin-available', 'checkout-available')
    } else if (canCI && !canCO) {
      // Checkin only (diagonal split: unavailable top-left, available bottom-right)
      classes.push('selectable', 'checkin-available')
    } else if (!canCI && canCO) {
      // Checkout only (diagonal split: available top-left, unavailable bottom-right)
      classes.push('checkout-available')
    } else if (canStay && !canCI && !canCO) {
      classes.push('pass-through')
    } else if (ciBooked || coBooked) {
      if (ciBooked) classes.push('checkin-booked')
      if (coBooked) classes.push('checkout-booked')
    } else if (!canCI && !canCO && !canStay) {
      classes.push('disabled', 'unavailable')
    } else {
      // Blocked combinations
      if (!canCI) classes.push('checkin-blocked')
      if (!canCO) classes.push('checkout-blocked')
    }

    if (calendarUtils.isToday(dateStr)) {
      classes.push('today')
    }

    return classes.join(' ')
  }, [calendarUtils, availability, allowSelectDates, selectedCheckin, selectedCheckout, hoveredDate])

  const getPriceText = useCallback((dateStr: string): string | null => {
    if (!showPrice) return null
    const opts = calendarUtils.dateOptions(dateStr)
    if (!opts) return null
    if (calendarUtils.isUnavailable(dateStr)) return null

    const isBooked = calendarUtils._checkinBooked(dateStr) || calendarUtils._checkoutBooked(dateStr)
    if (isBooked && !showBookedPrice) return null

    if (opts.price) {
      return formatPrice(opts.price, currency, locale)
    }
    return null
  }, [calendarUtils, availability, showPrice, showBookedPrice, currency, locale])

  const days = useMemo(() => {
    const cells: Array<{ key: string; dateStr: string | null; dayNum: number }> = []
    const TOTAL_CELLS = 42 // Always 6 rows x 7 columns to prevent layout shift

    // Empty cells for offset
    for (let i = 0; i < startOffset; i++) {
      cells.push({ key: `empty-start-${i}`, dateStr: null, dayNum: 0 })
    }

    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({ key: dateStr, dateStr, dayNum: d })
    }

    // Trailing empty cells to fill 42
    const remaining = TOTAL_CELLS - cells.length
    for (let i = 0; i < remaining; i++) {
      cells.push({ key: `empty-end-${i}`, dateStr: null, dayNum: 0 })
    }

    return cells
  }, [year, month, daysInMonth, startOffset])

  // #1548: `role="grid"` needs rows between the grid and its cells. The row
  // wrappers are `display: contents` so the 42 cells stay direct participants
  // of the CSS grid and the layout is unchanged — see `.days-grid > [role=row]`
  // in calendar.css.
  const weeks = useMemo(() => {
    const rows: Array<typeof days> = []
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7))
    return rows
  }, [days])

  /**
   * Spoken name for a day: the full date and the price the cell shows, plus why
   * it can't be picked. The price rides in the name because an aria-label hides
   * the cell's own text, and the visible price span is aria-hidden so the name
   * still contains every visible word (WCAG 2.5.3, #1865).
   */
  const getDayLabel = useCallback((dateStr: string, classNames: string, priceText: string | null): string => {
    const spoken = calendarUtils.spokenDate(dateStr, locale)
    const date = priceText ? `${spoken}, ${priceText}` : spoken
    if (/\bunavailable\b/.test(classNames)) return `${date}, unavailable`
    if (/\bnotice-blocked\b/.test(classNames)) return `${date}, too soon to book`
    if (/\bminstay-blocked\b/.test(classNames)) return `${date}, shorter than the minimum stay`
    if (/\brange-start\b/.test(classNames)) return `${date}, selected check-in`
    if (/\brange-end\b/.test(classNames)) return `${date}, selected check-out`
    if (/\bdisabled\b/.test(classNames)) return `${date}, not selectable`
    // A booked or trapped night, and the turnover halves: the same words
    // calendar-v2 speaks for them, so a booked day never sounds open.
    if (/\btrapped\b/.test(classNames) || (/\bcheckin-booked\b/.test(classNames) && /\bcheckout-booked\b/.test(classNames))) {
      return `${date}, unavailable`
    }
    const canIn = /\bcheckin-available\b/.test(classNames)
    const canOut = /\bcheckout-available\b/.test(classNames)
    if (canOut && !canIn) return `${date}, check-out only`
    if (canIn && !canOut) return `${date}, check-in only`
    return date
  }, [calendarUtils, locale])

  const handleDayClick = useCallback((dateStr: string, element: HTMLElement) => {
    if (!onDayClick) return
    onDayClick(dateStr, element)
  }, [onDayClick])

  const handleDayHover = useCallback((dateStr: string | null, element?: HTMLElement) => {
    if (!allowSelectDates || !onDayHover) return
    onDayHover(dateStr, element)
  }, [allowSelectDates, onDayHover])

  const headingId = `hr-cal-month-${year}-${month}`

  return (
    <div className="month" data-y={year} data-m={month}>
      <div className="calendar-heading month-header" id={headingId}>{monthLabel}</div>
      <div className="calendar-table" role="grid" aria-labelledby={headingId}>
        <div className="week-days weekdays" role="row">
          {weekdayLabels.map((label) => (
            <div key={label} className="day-name weekday" role="columnheader">{label}</div>
          ))}
        </div>
        <div className="days-grid" role="rowgroup">
          {weeks.map((week, wi) => (
            // A week of padding only (the 42-cell grid's trailing rows) is
            // hidden whole: a row with no exposed cell breaks role="grid" (#1865).
            <div key={`w-${wi}`} className="days-row" role="row" aria-hidden={week.every((d) => !d.dateStr) || undefined}>
              {week.map(({ key, dateStr, dayNum }) => {
                if (!dateStr) {
                  return <div key={key} className="day" role="gridcell" aria-hidden="true" />
                }

                const classNames = getDayClassNames(dateStr)
                const priceText = getPriceText(dateStr)
                const isDisabled = /\bdisabled\b/.test(classNames)
                const isSelected = /\bselected\b/.test(classNames)
                const describes = tooltipDate === dateStr && tooltipId ? tooltipId : undefined

                return (
                  <div
                    key={key}
                    className={classNames}
                    data-date={dateStr}
                    role="gridcell"
                    // One tabstop for the whole calendar (roving tabindex).
                    tabIndex={allowSelectDates && focusedDate === dateStr ? 0 : -1}
                    aria-label={getDayLabel(dateStr, classNames, priceText)}
                    aria-disabled={isDisabled || undefined}
                    aria-selected={allowSelectDates ? isSelected : undefined}
                    aria-describedby={describes}
                    onClick={(e) => handleDayClick(dateStr, e.currentTarget as HTMLElement)}
                    onKeyDown={(e) => onDayKeyDown?.(dateStr, e)}
                    onFocus={(e) => onDayFocus?.(dateStr, e.currentTarget as HTMLElement)}
                    /* Pointer events, gated PER EVENT by pointerType (toolkit
                       calendar-hover-tips parity): touch pointers never drive
                       hover, so tap behavior is unchanged and touchscreen
                       laptops with a mouse keep their hover tips. */
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'touch') return
                      handleDayHover(dateStr, e.currentTarget as HTMLElement)
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType === 'touch') return
                      handleDayHover(null)
                    }}
                  >
                    <span className="date">{dayNum}</span>
                    {priceText && <span className="price" aria-hidden="true">{priceText}</span>}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
